import { useNavigate, useParams } from "@tanstack/react-router";
import type { SetupChecklistItem } from "@unbox/api";
import { Rocket02 } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCompleteSetupItem,
  useDismissSetupChecklist,
  useIgnoreSetupItem,
  useMarkSetupChecklistSeen,
  useSetupChecklist,
} from "@/api/setup-checklist";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { usePermissions } from "@/hooks/use-permissions";
import type { SetupItemDefinition } from "../lib/setup-items";
import { setupSearchFor } from "../lib/setup-items";
import { SetupChecklistPanel, type SetupPanelVariant } from "./setup-checklist-panel";
import { SetupProgressRing } from "./setup-progress-ring";

type Props = { currentUserId?: string; variant?: SetupPanelVariant };

export function SetupChecklist({ currentUserId, variant = "dark" }: Props) {
  const enabled = useFeatureFlag("F-home-setup-checklist");
  const newCollabsGroups = useFeatureFlag("F-new-collabs-groups");
  const { canConfigureSafety } = usePermissions();
  const navigate = useNavigate();
  const routeParams = useParams({ strict: false }) as { merchantId?: string; workspaceId?: string };

  const { data: state } = useSetupChecklist(enabled);
  const complete = useCompleteSetupItem();
  const ignore = useIgnoreSetupItem();
  const markSeen = useMarkSetupChecklistSeen();
  const dismiss = useDismissSetupChecklist();

  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const autoOpened = useRef(false);
  const fabRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const settled = !!state?.summary.settled;

  const closePanel = useCallback(() => {
    setOpen(false);
    if (!settled) return;
    setDismissed(true);
    dismiss.mutate();
  }, [settled, dismiss]);

  useEffect(() => {
    if (!state?.autoOpen || autoOpened.current) return;
    autoOpened.current = true;
    setOpen(true);
    markSeen.mutate();
  }, [state?.autoOpen, markSeen]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) closePanel();
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, closePanel]);

  useEffect(() => {
    const scroller = fabRef.current?.parentElement?.closest<HTMLElement>(".overflow-y-auto");
    if (!scroller) return;

    const measure = () => setScrollbarWidth(Math.max(0, scroller.offsetWidth - scroller.clientWidth));

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [state]);

  if (!enabled || !state || state.dismissed || dismissed) return null;
  if (settled && !open) return null;

  const visit = (definition: SetupItemDefinition) => {
    const base = routeParams.workspaceId ? "/workspace/$workspaceId/merchant/$merchantId" : "/merchant/$merchantId";

    navigate({
      to: definition.destination(base, { newCollabsGroups }),
      params: routeParams,
      search: setupSearchFor(definition.item),
    });
  };

  const offsetRight = `calc(1.5rem + ${scrollbarWidth}px)`;

  return (
    <>
      <div
        ref={fabRef}
        className="fixed bottom-6 z-50"
        style={{ right: offsetRight, visibility: open ? "hidden" : "visible" }}
      >
        <SetupProgressRing total={state.summary.active} done={state.summary.done} />

        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-label="Setup Unbox"
          title="Setup Unbox"
          className="relative inline-flex size-12 cursor-pointer items-center justify-center rounded-full bg-white text-gray-900 shadow-lg transition-transform duration-150 hover:scale-105 active:scale-95"
        >
          <Rocket02 className="size-5" />
          {!open && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 size-3 animate-setup-nudge rounded-full bg-brand-500"
            />
          )}
        </button>
      </div>

      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-6 z-[60] flex w-[540px] animate-setup-panel-in flex-col overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
          style={{ right: offsetRight, height: "min(700px, calc(100vh - 120px))" }}
        >
          <SetupChecklistPanel
            state={state}
            variant={variant}
            currentUserId={currentUserId}
            canComplete={canConfigureSafety}
            destinationFor={visit}
            onComplete={(item: SetupChecklistItem) => complete.mutate(item)}
            onIgnore={(item: SetupChecklistItem, ignored: boolean) => ignore.mutate({ item, ignored })}
            onClose={closePanel}
          />
        </div>
      )}
    </>
  );
}
